<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAppStore } from '../../composables/useAppStore'
import { useGlobalSearch } from '../../composables/useGlobalSearch'
import { formatCurrency, formatDate } from '../../lib/date'
import BaseBadge from './BaseBadge.vue'
import BaseModal from './BaseModal.vue'
import EmptyState from './EmptyState.vue'
import SearchInput from './SearchInput.vue'

const router = useRouter()
const { state: appState, calculatedShifts, payoutPeriods } = useAppStore()
const { state, close } = useGlobalSearch()
const searchBox = ref(null)
const deferredQuery = ref('')
let debounceTimer = null

watch(() => state.query, (value) => {
  window.clearTimeout(debounceTimer)
  debounceTimer = window.setTimeout(() => {
    deferredQuery.value = String(value || '').trim().toLocaleLowerCase('ru-RU')
  }, 140)
})

watch(() => state.open, async (open) => {
  if (!open) return
  deferredQuery.value = String(state.query || '').trim().toLocaleLowerCase('ru-RU')
  await nextTick()
  searchBox.value?.focus?.()
})

onBeforeUnmount(() => window.clearTimeout(debounceTimer))

function conditionNames(shift) {
  return (shift.conditionCodes || [])
    .map((code) => appState.conditions.find((item) => item.code === code)?.title || code)
    .join(' ')
}

function searchableDate(date) {
  return `${date || ''} ${formatDate(date)}`.toLocaleLowerCase('ru-RU')
}

const shiftResults = computed(() => {
  const query = deferredQuery.value
  if (!query) return []
  return calculatedShifts.value.filter((item) => [
    searchableDate(item.shift.date),
    item.shift.comment,
    item.shift.startTime,
    item.shift.endTime,
    conditionNames(item.shift),
    String(item.shift.orders),
    String(item.calculation.net)
  ].join(' ').toLocaleLowerCase('ru-RU').includes(query)).slice(0, 10)
})

const payoutResults = computed(() => {
  const query = deferredQuery.value
  if (!query) return []
  return payoutPeriods.value.filter((period) => [
    searchableDate(period.start),
    searchableDate(period.end),
    searchableDate(period.payoutDate),
    searchableDate(period.payout?.paidAt),
    period.payout?.comment,
    period.statusMeta?.label,
    String(period.summary.net),
    String(period.actualAmount)
  ].join(' ').toLocaleLowerCase('ru-RU').includes(query)).slice(0, 10)
})

const hasResults = computed(() => shiftResults.value.length || payoutResults.value.length)

async function go(to) {
  close()
  await router.push(to)
}
</script>

<template>
  <BaseModal :open="state.open" title="Поиск по ДоходПро" description="Смены, комментарии, условия, расчётные периоды и выплаты" size="lg" @request-close="close">
    <SearchInput ref="searchBox" v-model="state.query" placeholder="Например: дождь, 10.07.2026, недоплата или комментарий" autofocus />

    <div v-if="deferredQuery && hasResults" class="mt-5 space-y-6">
      <section v-if="shiftResults.length">
        <h3 class="mb-2 text-xs font-black uppercase tracking-[.16em] text-app-muted">Смены</h3>
        <div class="divide-y divide-app-border overflow-hidden rounded-app-control border border-app-border">
          <button v-for="item in shiftResults" :key="item.shift.id" type="button" class="flex w-full items-center gap-3 bg-white px-4 py-3 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-brand-100" @click="go({ path: '/shifts', query: { shift: item.shift.id } })">
            <div class="min-w-0 flex-1">
              <p class="truncate text-sm font-black text-app-text">{{ formatDate(item.shift.date) }} · {{ item.shift.startTime }}–{{ item.shift.endTime }}</p>
              <p class="truncate text-xs font-semibold text-app-muted">{{ item.shift.comment || conditionNames(item.shift) || `${item.calculation.orders} заказов` }}</p>
            </div>
            <b class="shrink-0 text-sm text-app-text">{{ formatCurrency(item.calculation.net, appState.settings.currency) }}</b>
          </button>
        </div>
      </section>

      <section v-if="payoutResults.length">
        <h3 class="mb-2 text-xs font-black uppercase tracking-[.16em] text-app-muted">Выплаты</h3>
        <div class="divide-y divide-app-border overflow-hidden rounded-app-control border border-app-border">
          <button v-for="period in payoutResults" :key="period.key" type="button" class="flex w-full items-center gap-3 bg-white px-4 py-3 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-brand-100" @click="go({ path: '/payouts', query: { period: period.key } })">
            <div class="min-w-0 flex-1">
              <p class="truncate text-sm font-black text-app-text">{{ formatDate(period.start) }} – {{ formatDate(period.end) }}</p>
              <p class="truncate text-xs font-semibold text-app-muted">{{ period.payout?.comment || `Ожидаемая дата: ${formatDate(period.payoutDate)}` }}</p>
            </div>
            <div class="shrink-0 text-right"><b class="block text-sm text-app-text">{{ formatCurrency(period.summary.net, appState.settings.currency) }}</b><BaseBadge class="mt-1" :tone="period.statusMeta.tone">{{ period.statusMeta.label }}</BaseBadge></div>
          </button>
        </div>
      </section>
    </div>

    <EmptyState v-else-if="deferredQuery" class="mt-5" title="Ничего не найдено" text="Попробуйте другую дату, условие, статус выплаты или часть комментария." />
    <div v-else class="mt-5 rounded-app-control bg-slate-50 p-4 text-sm font-semibold leading-6 text-app-muted">
      Введите дату, комментарий, условие смены, статус или расчётный период. Поиск выполняется локально и не отправляет данные в интернет.
    </div>
  </BaseModal>
</template>
